<template>
  <div
      v-if="pageContent"
      :data-page-type="pageType"
      @click="closeSidebar"
  >
    <transition name="page-expand">
      <component
          :is="pageContent"
          v-show="status !== 1 && page"
      />
    </transition>
    <BaseLoader v-if="status === 1"/>
  </div>
</template>

<script lang="ts" setup>
import MetaHandler from '@/classes/MetaHandler';
import {
  defineComponent,
  shallowRef,
  computed,
  nextTick,
  reactive,
  watch,
  ref,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import PageInterface from '@/interfaces/PageInterface';

const props = defineProps<{
  noFetch?: boolean
}>();

const route = useRoute();
const store = useStore();
const pageType = computed(() => route.meta.type);
const show = reactive({ value: false });
const slug = computed(() => route.params.pageSlug);
const isFirstFetch = ref(true);

const toTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

const fetch = () => {
  if (props.noFetch && isFirstFetch.value) {
    isFirstFetch.value = false;

    if (window.ioInitialPageData) {
      delete window.ioInitialPageData;
    }

    return;
  }

  const appContent = document.querySelector('#ioapp-content');

  if (isFirstFetch.value && appContent) {
    isFirstFetch.value = false;

    return;
  }

  if (appContent) {
    appContent.remove();
  }

  store.dispatch('page/fetch', {
    slug: slug.value,
    pageId: route.query.page_id,
    preview: route.query.preview === 'true',
    previewId: route.query.preview_id,
  });
};

watch(() => slug.value, () => {
  fetch();
}, {
  immediate: true,
});

const page = computed<PageInterface | null>(() => store.getters['page/page']);
const status = computed(() => store.getters['page/status']);
const pageContent = shallowRef();

const updateAdminBar = () => {
  const $wpAdminBarCustomizer = document.querySelector('#wp-admin-bar-customize > a');
  const $wpAdminBarEdit = document.querySelector('#wp-admin-bar-edit > a');

  if ($wpAdminBarCustomizer) {
    $wpAdminBarCustomizer.setAttribute('href', `/wp-admin/customize.php?url=${window.location.origin}${route.path}`);
  }

  if ($wpAdminBarEdit && page.value) {
    $wpAdminBarEdit.setAttribute('href', `/wp-admin/post.php?post=${page.value.id}&action=edit`);
    $wpAdminBarEdit.innerHTML = page.value.postType?.labels.edit_item || $wpAdminBarEdit.innerHTML;
  } else if (page.value && page.value.id) {
    const $wpAdminBarRoot = document.querySelector('#wp-admin-bar-root-default');

    if ($wpAdminBarRoot) {
      const editLink = document.createElement('a');
      editLink.className = 'ab-item';
      editLink.href = page.value.editLink || `/wp-admin/post.php?post=${page.value.id}&action=edit`;
      editLink.innerText = page.value.postType?.labels.edit_item || 'Dings bearbeiten';

      const editBar = document.createElement('li');
      editBar.id = 'wp-admin-bar-edit';

      editBar.appendChild(editLink);
      $wpAdminBarRoot.appendChild(editBar);
    }
  }
};

const updateMeta = () => {
  if (!page.value) return;

  const title = page.value?.meta?.title || page.value?.title || '';
  const is404 = page.value?.is404 || true;
  const blogPublic = page.value?.blogPublic || false;
  const description = page.value?.meta?.description;
  const yoastHtml = page.value?.meta?.yoastHead?.html;

  let headerHtml = document.head.innerHTML.replace(/<!-- This site is optimized with the Yoast SEO plugin v([1-9]\d*(\.[1-9]\d*){0,3}) - https:\/\/yoast.com\/wordpress\/plugins\/seo\/ -->((.|[\n|\r|\r\n])*?)<!-- \/ Yoast SEO plugin. -->?/sg, '');

  headerHtml = headerHtml.replace(/<title>(.*?)<\/title>/sg, `<title>${title}</title>`);
  headerHtml = headerHtml.replace(/<meta name="description" content="(.*?)">/sg, `<meta name="description" content="${description}">`);

  const hasHeaderRobots = /<meta name="robots" content="(.*?)">/sg.test(headerHtml);
  const hasYoastRobots = /<meta name="robots" content="(.*?)">/sg.test(yoastHtml || '');

  if (hasHeaderRobots && hasYoastRobots) {
    headerHtml = headerHtml.replace(/<meta name="robots" content="(.*?)">/sg, '');
  } else if (hasHeaderRobots && !hasYoastRobots) {
    headerHtml = headerHtml.replace(/<meta name="robots" content="(.*?)">/sg, `<meta name="robots" content="${blogPublic ? 'index, follow' : 'noindex, nofollow'}">`);
  } else if (!hasHeaderRobots && !hasYoastRobots) {
    if (is404 || !blogPublic) {
      headerHtml += '<meta name="robots" content="noindex, follow">';
    } else {
      headerHtml += '<meta name="robots" content="index, follow">';
    }
  }

  headerHtml += yoastHtml || '';

  document.head.innerHTML = headerHtml;
};

watch(() => page.value, () => {
  MetaHandler.clearAll();

  pageContent.value = defineComponent({
    template: `<div class="page-content">${page.value?.content}</div>`,
  });

  nextTick(() => {
    show.value = true;
    setTimeout(() => {
      window.runIoEqualize();
      window.dispatchEvent(new Event('fetched'));
    }, 100);
    toTop();
  });

  const $wpAdminBar = document.querySelector('#wpadminbar');
  if ($wpAdminBar) {
    updateAdminBar();
  }

  updateMeta();
}, {
  immediate: true,
});

const closeSidebar = () => {
  store.dispatch('sidebar/close');
};
</script>

<style lang="scss">
.page-content {
  > section + p:empty {
    display: none;
  }
}
</style>
